import { FieldLayerWellsGroup, LayerInfo } from '@/api/fields';
import { deleteLayer } from '@/api/layers';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { CreateLayerModal } from './CreateLayerModal';
import { LayerProductionModal } from './LayerProductionModal';
import { LayerDetailModal } from './LayerDetailModal';

interface LayersListProps {
  layers: LayerInfo[];
  fieldId: string;
  wells: FieldLayerWellsGroup[];
  canManage?: boolean;
  onLayersUpdated?: () => void;
}

export const LayersList: React.FC<LayersListProps> = ({
  layers,
  fieldId,
  wells,
  canManage = false,
  onLayersUpdated,
}) => {
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [selectedLayerForProduction, setSelectedLayerForProduction] = useState<LayerInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [layerToDelete, setLayerToDelete] = useState<LayerInfo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleLayerClick = (layerId: string) => {
    setSelectedLayerId(layerId);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedLayerId(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleShowProduction = (layer: LayerInfo) => {
    setSelectedLayerForProduction(layer);
    setIsProductionModalOpen(true);
  };

  const handleCloseProductionModal = () => {
    setIsProductionModalOpen(false);
    setSelectedLayerForProduction(null);
  };

  const handleDeleteClick = (layer: LayerInfo) => {
    setLayerToDelete(layer);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!layerToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteLayer(layerToDelete.id);
      setDeleteDialogOpen(false);
      setLayerToDelete(null);
      onLayersUpdated?.();
    } catch (err) {
      console.error('Failed to delete reservoir:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'oil':
        return 'success';
      case 'gas':
        return 'info';
      case 'oil and gas':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter layers based on search term
  const filteredLayers = useMemo(() => {
    if (!searchTerm.trim()) {
      return layers;
    }
    const searchLower = searchTerm.toLowerCase();
    return layers.filter((layer) =>
      layer.name.toLowerCase().includes(searchLower) ||
      (layer.description && layer.description.toLowerCase().includes(searchLower)) ||
      (layer.type && layer.type.toLowerCase().includes(searchLower))
    );
  }, [layers, searchTerm]);

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        TransitionProps={{ timeout: 100 }}
        sx={{
          '& .MuiAccordionDetails-root': {
            transition: 'none',
          },
          '& .MuiCollapse-root': {
            transition: 'height 100ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="reservoirs-content"
          id="reservoirs-header"
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={2}>
            <Typography variant="h6">Reservoirs ({layers.length})</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
              <TextField
                placeholder="Search by name, description, or type..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ flexGrow: 1 }}
              />
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  Add Reservoir
                </Button>
              )}
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Production</TableCell>
                    {canManage && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLayers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 5 : 4} align="center">
                        <Typography variant="body2" color="textSecondary">
                          {searchTerm ? 'No reservoirs found matching your search' : 'No reservoirs found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLayers.map((layer) => (
                      <TableRow key={layer.id}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleLayerClick(layer.id)}
                            sx={{ textDecoration: 'none', cursor: 'pointer' }}
                          >
                            {layer.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {layer.type && (
                            <Chip
                              label={layer.type}
                              color={getTypeColor(layer.type) as any}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {layer.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleShowProduction(layer)}
                          >
                            Show
                          </Button>
                        </TableCell>
                        {canManage && (
                          <TableCell align="right">
                            <Tooltip title="Delete reservoir">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(layer)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Reservoir Detail Modal */}
      <LayerDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        layerId={selectedLayerId}
        onLayerUpdated={onLayersUpdated}
      />

      {/* Create Reservoir Modal */}
      <CreateLayerModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        fieldId={fieldId}
        onLayerCreated={onLayersUpdated}
      />

      {selectedLayerForProduction && (
        <LayerProductionModal
          open={isProductionModalOpen}
          onClose={handleCloseProductionModal}
          layerId={selectedLayerForProduction.id}
          layerName={selectedLayerForProduction.name}
          wells={wells}
          canManage={canManage}
          onDataUpdated={onLayersUpdated}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete reservoir?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will hide &quot;{layerToDelete?.name}&quot; from the list. Associated wells and data will be preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
